var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import EventEmitter from 'events';
import { MarginInfoGroup } from './MarginInfoGroup';
import { MarginInfoItem } from './MarginInfoItem';
import { CommissionOperationType, CommissionTypes } from '@shared/utils/Commission/CommissionEnums';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { MarginInfoItemType } from './MarginInfoEnums';
import { MarginInfoUtils } from './MarginInfoUtils';
import { QuotingType } from '@shared/utils/Instruments/QuotingType';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { Account } from '../../cache/Account';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DirectMarginMessage } from '@shared/utils/DirectMessages/DirectMarginMessage';
import { StringUtils } from '@shared/utils/StringUtils';
import { CommissionItem } from '../../cache/Commissions/CommissionItem';
import { Resources } from '@shared/localizations/Resources';
import { isMarginResponseDepType } from './MarginInfoKeys';
export class MarginInfoData {
    constructor(value, formattedValue) {
        this.value = value;
        this.formattedValue = formattedValue;
    }
}
export class MarginInfoWrapper {
    constructor() {
        this.FULL_REFRESH_TIMER = 30000;
        this.TICK_REFRESH_TIMER = 500;
        this._isWorking = false;
        this._eventEmitter = new EventEmitter();
        this.onFullRefresh = this.onFullRefresh.bind(this);
        this.onTickRefresh = this.onTickRefresh.bind(this);
        this.onSuccessRequest = this.onSuccessRequest.bind(this);
        this.onErrorRequest = this.onErrorRequest.bind(this);
    }
    dispose() {
        this.stop();
    }
    run() {
        if (this._isWorking) {
            return;
        }
        this._isWorking = true;
        this._fullRefreshTimer = setInterval(this.onFullRefresh, this.FULL_REFRESH_TIMER);
        this._tickRefreshTimer = setInterval(this.onTickRefresh, this.TICK_REFRESH_TIMER);
        this.onFullRefresh();
    }
    stop() {
        if (this._fullRefreshTimer) {
            clearInterval(this._fullRefreshTimer);
            this._fullRefreshTimer = null;
        }
        if (this._tickRefreshTimer) {
            clearInterval(this._tickRefreshTimer);
            this._tickRefreshTimer = null;
        }
        this._isWorking = false;
    }
    subscribeToGroupUpdate(callback) {
        this._eventEmitter.addListener('groupUpdate', callback);
    }
    unsubscribeFromGroupUpdate(callback) {
        this._eventEmitter.removeListener('groupUpdate', callback);
    }
    subscribeToItemUpdate(callback) {
        this._eventEmitter.addListener('itemUpdate', callback);
    }
    unsubscribeFromItemUpdate(callback) {
        this._eventEmitter.removeListener('itemUpdate', callback);
    }
    getMarginResponse() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.sendMarginRequest();
        });
    }
    getInfoAsync(response) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const params = this._parameters;
                const groups = this.ProcessRequest(response, params);
                return groups;
            }
            catch (error) {
                return [];
            }
        });
    }
    GetMarginAmount(parameters) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((parameters === null || parameters === void 0 ? void 0 : parameters.account) == null) {
                return NaN;
            }
            const response = yield parameters.instrument.DataCache.SendMarginRequest([parameters]);
            const msg = (this._message = response);
            const amount = parameters.operation === OperationType.Buy ? msg === null || msg === void 0 ? void 0 : msg.AmountBuy : msg === null || msg === void 0 ? void 0 : msg.AmountSell;
            return amount !== null && amount !== void 0 ? amount : NaN;
        });
    }
    getEstAvailableCash() {
        const message = this._message;
        if (isNullOrUndefined(message) || isNullOrUndefined(message.EstAvailableCash)) {
            return null;
        }
        return message.EstAvailableCash;
    }
    getFreeCommission() {
        const message = this._message;
        if (isNullOrUndefined(message) || isNullOrUndefined(message.FreeCommission)) {
            return null;
        }
        return message.FreeCommission;
    }
    fullRefresh() {
        const params = this._parameters;
        if (!params) {
            return;
        }
        this.sendMarginRequest()
            .then((response) => {
            this.onSuccessRequest(response, params);
        })
            .catch(this.onErrorRequest);
    }
    onFullRefresh() {
        this.fullRefresh();
    }
    onTickRefresh() {
        this.tickRefresh();
    }
    sendMarginRequest() {
        return __awaiter(this, void 0, void 0, function* () {
            const params = this._parameters;
            if (isNullOrUndefined(params)) {
                return;
            }
            return yield params.instrument.DataCache.SendMarginRequest([params]);
        });
    }
    onSuccessRequest(response, parameters) {
        const groups = this.ProcessRequest(response, parameters);
        this._eventEmitter.emit('groupUpdate', groups);
    }
    ProcessRequest(response, parameters) {
        const lastMessage = response;
        this._message = lastMessage;
        const groupDict = new Map();
        const itemTypes = Object.values(MarginInfoItemType); /* .filter(v => isValidNumber(+v)) */
        for (const element of itemTypes) {
            const itemType = Number(element);
            const data = this.getDataByType(itemType, parameters, lastMessage);
            if (!data || MarginInfoUtils.isItemHidden(itemType)) {
                continue;
            }
            const groupType = MarginInfoUtils.getGroupForItem(itemType);
            if (!groupDict.has(groupType)) {
                const group = new MarginInfoGroup();
                group.id = groupType;
                group.title = MarginInfoUtils.getGroupText(groupType, parameters.account.assetName);
                groupDict.set(groupType, group);
            }
            const group = groupDict.get(groupType);
            if (!group.items) {
                group.items = new Array();
            }
            const item = new MarginInfoItem();
            item.id = itemType;
            item.isColored = MarginInfoUtils.isColoredItem(itemType);
            item.title = MarginInfoUtils.getItemText(itemType);
            item.formattedValue = data.formattedValue;
            item.value = data.value;
            group.items.push(item);
        }
        return Array.from(groupDict.values());
    }
    onErrorRequest(_error) {
        // TODO handle error
    }
    tickRefresh() {
        const params = this._parameters;
        if (!params || !this._message) {
            return;
        }
        const itemsByGroup = new Map();
        const itemTypes = Object.values(MarginInfoItemType); /* .filter(v => isValidNumber(+v)) */
        for (const element of itemTypes) {
            const itemType = Number(element);
            if (MarginInfoUtils.isAccountFeatureType(itemType)) {
                const data = this.getDataByType(itemType, params, this._message);
                if (!data || MarginInfoUtils.isItemHidden(itemType)) {
                    continue;
                }
                const group = MarginInfoUtils.getGroupForItem(itemType);
                if (!itemsByGroup.has(group)) {
                    itemsByGroup.set(group, new Array());
                }
                const items = itemsByGroup.get(group);
                const item = new MarginInfoItem();
                item.id = itemType;
                item.isColored = MarginInfoUtils.isColoredItem(itemType);
                item.title = MarginInfoUtils.getItemText(itemType);
                item.formattedValue = data.formattedValue;
                item.value = data.value;
                items.push(item);
            }
        }
        itemsByGroup.forEach((items, groupType) => {
            items.forEach((item) => {
                this._eventEmitter.emit('itemUpdate', groupType, item);
            });
        });
    }
    getDataByType(type, parameters, message) {
        var _a, _b;
        let value = NaN;
        let valueStr = '';
        const isMarginDep = isMarginResponseDepType(type);
        if (isMarginDep && !(message instanceof DirectMarginMessage)) {
            valueStr = Resources.getResource('general.Calculating');
            return new MarginInfoData(value, valueStr);
        }
        const isLong = parameters.operation === OperationType.Buy;
        const operationType = isLong ? CommissionOperationType.BUY : CommissionOperationType.SHORT;
        const targetGroup = isLong ? message.BuyGroup : message.SellGroup;
        switch (type) {
            case MarginInfoItemType.KEY_BALANCE:
                value = Account.GetAccountFeature(AccountFeature.Balance, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.Balance, parameters.account, parameters.account.assetBalanceDefault, null, true);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_AVAILABLE_FUNDS:
                value = Account.GetAccountFeature(AccountFeature.AvailableFunds, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.AvailableFunds, parameters.account, parameters.account.assetBalanceDefault, null, true);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_INCOMING_FUNDS:
                value = Account.GetAccountFeature(AccountFeature.IncomingFunds, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.IncomingFunds, parameters.account, parameters.account.assetBalanceDefault, null, true);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_MARGIN_AVAILABLE:
                value = Account.GetAccountFeature(AccountFeature.MarginAvailable, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.MarginAvailable, parameters.account, parameters.account.assetBalanceDefault, null, true);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_IMPACT_ON_FUNDS:
                value = targetGroup === null || targetGroup === void 0 ? void 0 : targetGroup.ImpactOnFunds;
                return !isNaN(value) ? new MarginInfoData(value, parameters.account.formatPrice(value, true)) : null;
            case MarginInfoItemType.KEY_IMPACT_ON_MARGIN:
                value = this.getImpactOnMarginValue(targetGroup === null || targetGroup === void 0 ? void 0 : targetGroup.AfterTradeMargin, parameters.account);
                return !isNaN(value) ? new MarginInfoData(value, parameters.account.formatPrice(value, true)) : null;
            case MarginInfoItemType.KEY_AFTER_TRADE_FUNDS:
                value = targetGroup === null || targetGroup === void 0 ? void 0 : targetGroup.AfterTradeFunds;
                return !isNaN(value) ? new MarginInfoData(value, parameters.account.formatPrice(value, true)) : null;
            case MarginInfoItemType.KEY_AFTER_TRADE_MARGIN:
                value = targetGroup === null || targetGroup === void 0 ? void 0 : targetGroup.AfterTradeMargin;
                return !isNaN(value) ? new MarginInfoData(value, parameters.account.formatPrice(value, true)) : null;
            case MarginInfoItemType.KEY_BLOCKED_FOR_STOCKS:
                value = Account.GetAccountFeature(AccountFeature.BlockedForStocks, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.BlockedForStocks, parameters.account, parameters.account.assetBalanceDefault, null, true);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_SPREAD_INIT_LOSS:
                value = this.getSpreadInitLossValue(parameters);
                valueStr = value ? parameters.account.formatPrice(value, true) : StringUtils.THREE_DASHES;
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_PL_PER_TICK:
                value = this.getPLPerTickValue(parameters);
                return !isNaN(value) ? new MarginInfoData(value, parameters.account.formatPrice(value, true)) : null;
            case MarginInfoItemType.KEY_ALLOW_SHORT_POSITIONS:
                valueStr = parameters.instrument.IsAllowShortPositions(parameters.productType)
                    ? MarginInfoUtils.ALLOW_SHOW_POSITIONS
                    : MarginInfoUtils.NOT_ALLOW_SHORT_POSITIONS;
                return new MarginInfoData(1, valueStr);
            case MarginInfoItemType.KEY_EST_AVAILABLE_CASH:
                value = this.getEstAvailableCash();
                valueStr = !isNullOrUndefined(value) ? parameters.account.formatPrice(value) : StringUtils.THREE_DASHES;
                return !isNullOrUndefined(value) ? new MarginInfoData(value, valueStr) : null;
            case MarginInfoItemType.KEY_FILL_PER_LOT:
                value = this.getFeeValue(CommissionTypes.PerLot, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_ORDER_PER_LOT:
                value = this.getAbsFeeValue(CommissionTypes.OrderPerLot, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
                value = this.getAbsFeeValue(CommissionTypes.OrderPerLotWithFixedPart, operationType);
                return !isNaN(value)
                    ? // && value !== 0  /* ← disabled due to #127046 */
                        new MarginInfoData(value, this.formatValue(value))
                    : null;
            case MarginInfoItemType.KEY_PER_FILL:
                value = this.getAbsFeeValue(CommissionTypes.PerFill, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_PER_TRANSACTION:
                value = this.getAbsFeeValue(CommissionTypes.PerTransaction, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_PER_PHONE_TRANSACTION:
                // Unused
                return null;
            case MarginInfoItemType.KEY_VAT:
                value = this.getAbsFeeValue(CommissionTypes.VAT, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_FILL_VOLUME:
                value = this.getAbsFeeValue(CommissionTypes.PerVolume, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_ORDER_VOLUME:
                value = this.getAbsFeeValue(CommissionTypes.PerOrderVolume, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_FILL_VOLUME_WITH_MIN_PD:
                value = this.getAbsFeeValue(CommissionTypes.VolumeWithMinPD, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_SHORT_SWAP_INFO:
                valueStr = message.ShortSwapInfo;
                if (!isValidString(valueStr)) {
                    return null;
                }
                return new MarginInfoData(NaN, this.formatValue(value, valueStr));
            case MarginInfoItemType.KEY_FREE_COMMISSION:
                value = this.getFreeCommission();
                valueStr = !isNullOrUndefined(value)
                    ? parameters.account.formatPrice(value, true)
                    : StringUtils.THREE_DASHES;
                return !isNullOrUndefined(value) ? new MarginInfoData(value, valueStr) : null;
            case MarginInfoItemType.KEY_TOTAL_FEES:
                value = this.isTotalFeeVisible() ? targetGroup === null || targetGroup === void 0 ? void 0 : targetGroup.TotalFee : NaN;
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.KEY_LONG_SWAP:
                value = (_a = message.BuyGroup) === null || _a === void 0 ? void 0 : _a.Swap;
                return new MarginInfoData(value, this.formatValue(value, StringUtils.THREE_DASHES));
            case MarginInfoItemType.KEY_SHORT_SWAP:
                value = (_b = message.SellGroup) === null || _b === void 0 ? void 0 : _b.Swap;
                return new MarginInfoData(value, this.formatValue(value, StringUtils.THREE_DASHES));
            case MarginInfoItemType.KEY_ORDER_CARE:
                value = this.getAbsFeeValue(CommissionTypes.ShortCommissionInfo, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            case MarginInfoItemType.BLOCKED_FOR_FIXED_INCOME:
                value = Account.GetAccountFeature(AccountFeature.BlockedForFixedIncome, parameters.account, parameters.account.assetBalanceDefault);
                valueStr = Account.GetAccountFeatureString(value, AccountFeature.BlockedForFixedIncome, parameters.account, parameters.account.assetBalanceDefault);
                return new MarginInfoData(value, valueStr);
            case MarginInfoItemType.KEY_FILL_FACE_VALUE:
                value = this.getAbsFeeValue(CommissionTypes.FillFaceValue, operationType);
                return !isNaN(value) ? new MarginInfoData(value, this.formatValue(value)) : null;
            default:
                return null;
        }
    }
    isTotalFeeVisible() {
        if (isValidNumber(this.getFreeCommission()))
            return true;
        const operationType = this._parameters.operation === OperationType.Buy
            ? CommissionOperationType.BUY
            : CommissionOperationType.SHORT;
        let count = 0;
        for (const type of CommissionItem.NumericCommissionTypes) {
            const feeValue = this.getFeeValue(type, operationType);
            if (isValidNumber(feeValue) && ++count > 1) {
                return true;
            }
        }
        return false;
    }
    getAbsFeeValue(commissionType, operationMode) {
        const value = this.getFeeValue(commissionType, operationMode);
        if (value) {
            return Math.abs(value);
        }
        else {
            return value;
        }
    }
    getFeeValue(commissionType, operationMode) {
        const commissionGroups = this.getCommissionGroups(commissionType, operationMode);
        if (!(commissionGroups === null || commissionGroups === void 0 ? void 0 : commissionGroups.length)) {
            return NaN;
        }
        let sumByOperationMode = 0;
        for (const element of commissionGroups) {
            sumByOperationMode += element.Amount;
        }
        return sumByOperationMode;
    }
    formatValue(value, defaultValue = '') {
        if (isNaN(value)) {
            return defaultValue;
        }
        return PriceFormatter.formatPrice(value, 2);
    }
    getSpreadInitLossValue(parameters) {
        const instrument = parameters.instrument;
        const account = parameters.account;
        const amountLots = parameters.amountLots;
        const spreadPlan = account === null || account === void 0 ? void 0 : account.DataCache.GetSpreadPlan(account);
        const operation = parameters.operation;
        if (!account || !instrument || !amountLots || !instrument.LastQuote) {
            return NaN;
        }
        const crossPrice = account === null || account === void 0 ? void 0 : account.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(account, instrument, operation);
        const ask = instrument.LastQuote.AskSpread_SP_Ins(spreadPlan, instrument);
        const bid = instrument.LastQuote.BidSpread_SP_Ins(spreadPlan, instrument);
        let result = -(ask - bid) * amountLots * crossPrice;
        result *= instrument.getLotSize();
        result *= instrument.GetTickCost();
        return result && result !== 0 ? result : NaN;
    }
    getPLPerTickValue(parameters) {
        const instrument = parameters.instrument;
        const account = parameters.account;
        const amountLots = parameters.amountLots;
        const operation = parameters.operation;
        if (!account || !instrument || !amountLots) {
            return NaN;
        }
        const crossPrice = account === null || account === void 0 ? void 0 : account.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(account, instrument, operation);
        let result;
        if (MarginInfoUtils.TICKCOST_INS_TYPES.includes(instrument.InstrType) &&
            instrument.QuotingType === QuotingType.TickCost_TickSize) {
            result = instrument.FuturesTickCoast * amountLots * crossPrice;
        }
        else {
            let price = parameters.price || parameters.stopPrice;
            if (!price) {
                const quote = instrument.GetLastQuote(QuoteValid.Valid);
                const isLong = operation === OperationType.Buy;
                if (quote) {
                    price = isLong ? quote.Ask : quote.Bid;
                }
            }
            const tickSize = instrument.LotSize * instrument.GetPointSize(price);
            result = tickSize * amountLots * crossPrice;
        }
        result *= instrument.GetFaceValueMultiplier(); /* #127528 */
        return result;
    }
    getImpactOnMarginValue(afterTradeMargin, account) {
        const marginAvailable = Account.GetAccountFeature(AccountFeature.MarginAvailable, account, account.assetBalanceDefault);
        if (!marginAvailable || !afterTradeMargin) {
            return NaN;
        }
        return afterTradeMargin - marginAvailable;
    }
    getCommissionGroups(commissionType, operationMode) {
        const message = this._message;
        if (!(message === null || message === void 0 ? void 0 : message.CommissionsByOperationMode)) {
            return null;
        }
        const commissionGroups = message.CommissionsByOperationMode[operationMode];
        const resultArr = [];
        if (commissionGroups) {
            for (const element of commissionGroups) {
                const commissionGroup = element;
                if (commissionGroup.CommissionType === commissionType) {
                    resultArr.push(commissionGroup);
                }
            }
        }
        return resultArr;
    }
}
